import { useCallback, useState } from 'react';
import axios, { isAxiosError } from 'axios';
import { useInitiateSmsMfa, useRegisterSmsMfa } from '@digiwedge/hooks-auth-web';
import { authApi } from '@digiwedge/idp-client';
import { useGlobalStore } from '@digiwedge/global-state';
import { getMessage } from '../i18n/messages.js';
const resolveIdpClient = () => {
    const client = authApi;
    const axiosInstance = client.axios ?? axios;
    const base = client.basePath?.replace(/\/$/, '') ?? '';
    const url = (path) => `${base}${path}`;
    return { axiosInstance, url };
};
const buildAuthHeaders = () => {
    const token = useGlobalStore.getState().token;
    const headers = { 'X-Requested-With': 'XMLHttpRequest' };
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
};
export const useMfaSetupHandlers = (options = {}) => {
    const { userId, onServerError, alreadyEnrolled, method = 'sms', email, tenantId } = options;
    const { registerSmsMfa, loading: registeringMfa } = useRegisterSmsMfa();
    const { initiateSmsMfa, loading: initiatingMfa } = useInitiateSmsMfa();
    const [emailSubmitting, setEmailSubmitting] = useState(false);
    const [emailVerifying, setEmailVerifying] = useState(false);
    const [smsVerifying, setSmsVerifying] = useState(false);
    const [whatsappVerifying, setWhatsappVerifying] = useState(false);
    const runInitiateFlow = useCallback(async (payload) => {
        if (!userId) {
            return { ok: false, reason: 'missing-user' };
        }
        if (method === 'email') {
            try {
                const { axiosInstance, url } = resolveIdpClient();
                await axiosInstance.post(url('/api/mfa/email/initiate'), { userId, secret: payload?.secret }, { withCredentials: true, headers: buildAuthHeaders() });
                return { ok: true };
            }
            catch (error) {
                return { ok: false, reason: 'error', error };
            }
        }
        if (method === 'whatsapp') {
            return { ok: false, reason: 'error', error: new Error('unsupported-resend-whatsapp') };
        }
        try {
            await initiateSmsMfa({ userId, secret: payload?.secret });
            return { ok: true };
        }
        catch (error) {
            return { ok: false, reason: 'error', error };
        }
    }, [initiateSmsMfa, method, userId]);
    const submitPhone = useCallback(async (dto) => {
        onServerError?.(null);
        const fallbackMessage = method === 'email'
            ? getMessage('en-US', 'genericError')
            : method === 'whatsapp'
                ? 'Unable to start WhatsApp MFA.'
                : getMessage('en-US', 'registerPhoneNumberError');
        const handleServerError = (error) => {
            if (isAxiosError(error)) {
                const data = error.response?.data;
                onServerError?.({
                    message: data?.message ?? fallbackMessage,
                    fieldErrors: data?.fieldErrors,
                });
            }
            else {
                onServerError?.({
                    message: fallbackMessage,
                });
            }
        };
        if (method === 'email') {
            if (!email) {
                onServerError?.({
                    message: 'Email is required to set up email-based MFA.',
                });
                return { ok: false, reason: 'missing-user' };
            }
            const startEmailFlow = async () => {
                const { axiosInstance, url } = resolveIdpClient();
                await axiosInstance.post(url('/api/mfa/email/register'), { email, secret: dto.secret }, { withCredentials: true, headers: buildAuthHeaders() });
            };
            setEmailSubmitting(true);
            try {
                await startEmailFlow();
                return { ok: true };
            }
            catch (error) {
                const isConflict = isAxiosError(error) && error.response?.status === 409;
                if (isConflict) {
                    const result = await runInitiateFlow({ secret: dto.secret });
                    if (!result.ok) {
                        if (result.reason === 'error') {
                            handleServerError(result.error);
                        }
                        else {
                            onServerError?.({
                                message: getMessage('en-US', 'loginMfaMissingUser'),
                            });
                        }
                    }
                    return result;
                }
                handleServerError(error);
                return { ok: false, reason: 'error', error };
            }
            finally {
                setEmailSubmitting(false);
            }
        }
        if (method === 'whatsapp') {
            if (!userId) {
                onServerError?.({
                    message: getMessage('en-US', 'loginMfaMissingUser'),
                });
                return { ok: false, reason: 'missing-user' };
            }
            if (!tenantId) {
                onServerError?.({
                    message: 'Missing tenant. Please try reloading and signing in again.',
                });
                return { ok: false, reason: 'missing-user' };
            }
            try {
                const { axiosInstance, url } = resolveIdpClient();
                await axiosInstance.post(url('/api/mfa/whatsapp/setup'), { userId, tenantId, whatsappNumber: dto.phoneNumber, secret: dto.secret }, { withCredentials: true, headers: buildAuthHeaders() });
                return { ok: true };
            }
            catch (error) {
                const isConflict = isAxiosError(error) && error.response?.status === 409;
                if (isConflict) {
                    return { ok: true };
                }
                handleServerError(error);
                return { ok: false, reason: 'error', error };
            }
        }
        if (alreadyEnrolled) {
            const result = await runInitiateFlow({ secret: dto.secret });
            if (!result.ok) {
                if (result.reason === 'error') {
                    handleServerError(result.error);
                }
                else {
                    onServerError?.({
                        message: getMessage('en-US', 'loginMfaMissingUser'),
                    });
                }
            }
            return result;
        }
        try {
            await registerSmsMfa(dto);
            return { ok: true };
        }
        catch (error) {
            const isConflict = isAxiosError(error) && error.response?.status === 409;
            if (isConflict) {
                const result = await runInitiateFlow({ secret: dto.secret });
                if (result.ok) {
                    return result;
                }
                if (result.reason === 'error') {
                    handleServerError(result.error);
                }
                else {
                    onServerError?.({
                        message: getMessage('en-US', 'loginMfaMissingUser'),
                    });
                }
                return result;
            }
            handleServerError(error);
            return { ok: false, reason: 'error', error };
        }
    }, [
        alreadyEnrolled,
        email,
        method,
        onServerError,
        registerSmsMfa,
        runInitiateFlow,
        tenantId,
        userId,
    ]);
    const submitCode = useCallback(async (code) => {
        if (!userId) {
            return { ok: false, reason: 'missing-user' };
        }
        if (method === 'email') {
            setEmailVerifying(true);
            try {
                const { axiosInstance, url } = resolveIdpClient();
                await axiosInstance.post(url('/api/mfa/email/register/verify'), { userId, code }, { withCredentials: true, headers: buildAuthHeaders() });
                return { ok: true };
            }
            catch (error) {
                return { ok: false, reason: 'error', error };
            }
            finally {
                setEmailVerifying(false);
            }
        }
        if (method === 'whatsapp') {
            try {
                setWhatsappVerifying(true);
                const { axiosInstance, url } = resolveIdpClient();
                await axiosInstance.post(url('/api/mfa/whatsapp/verify'), { userId, code }, { withCredentials: true, headers: buildAuthHeaders() });
                return { ok: true };
            }
            catch (error) {
                return { ok: false, reason: 'error', error };
            }
            finally {
                setWhatsappVerifying(false);
            }
        }
        try {
            setSmsVerifying(true);
            const { axiosInstance, url } = resolveIdpClient();
            await axiosInstance.post(url('/api/mfa/sms/register/verify'), { userId, code }, { withCredentials: true, headers: buildAuthHeaders() });
            return { ok: true };
        }
        catch (error) {
            return { ok: false, reason: 'error', error };
        }
        finally {
            setSmsVerifying(false);
        }
    }, [method, userId]);
    return {
        submitPhone,
        submitCode,
        requestExistingMfaCode: runInitiateFlow,
        registeringMfa: method === 'email' ? emailSubmitting : registeringMfa || initiatingMfa,
        verifyingMfa: method === 'email'
            ? emailVerifying
            : method === 'whatsapp'
                ? whatsappVerifying
                : smsVerifying,
    };
};
//# sourceMappingURL=useMfaSetupHandlers.js.map